import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Grid } from "@mui/material";
import axios from "axios";
import { GridExpandMoreIcon } from "@mui/x-data-grid";

//import constants
import BASE_URL from "../../../../constants/baseUrls";
import { useTranslation } from "react-i18next";

const FareRulesModal = ({ fareSourceCode, uniqueId, fareRules, setFareRules }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const accessToken = localStorage.getItem("accessToken");
    const { t } = useTranslation();

    const getFareRules = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${BASE_URL}/api/v2/reserve/foreign/flight/rules/`, {
                params: {
                    fare_source_code: fareSourceCode,
                    unique_id: uniqueId,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.data.data && response.data.data.log_id) {
                // This is the case where we get a log_id instead of fare rules
                setError(`${t("Toast Messages.Information Unavailable")} Log ID: ${response.data.data.log_id}`);
            } else if (Array.isArray(response.data.data) && response.data.data.length > 0) {
                // This is the case where we get the fare rules as expected
                setFareRules(response.data.data);
            } else {
                setError(t("retry"));
            }
        } catch (error) {
            setError(t("retry"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((fareSourceCode || uniqueId) && !fareRules?.length) getFareRules();
    }, [fareSourceCode, uniqueId]);

    return (
        <Grid
            container
            item
            xs={12}
            rowGap={5}
            justifyContent="center"
            sx={{ padding: "1rem", border: "1px solid var(--light-gray-color)", borderRadius: "0.625rem" }}
        >
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
                    <CircularProgress color="inherit" size={24} />
                </Box>
            ) : error ? (
                <Typography color="error" onClick={getFareRules} sx={{ cursor: "pointer", textDecoration: "underline" }}>
                    {error}
                </Typography>
            ) : fareRules?.length > 0 ? (
                fareRules?.map((fareRule, index) => (
                    <Grid key={index} container item xs={12} rowGap={1} alignItems="flex-start">
                        <Grid container item xs={12} columnGap={10} sx={{ fontSize: { xs: "0.75rem", md: "1rem" } }}>
                            <span>
                                {t("airline")}: {fareRule.airline || "-"}
                            </span>
                            <span>
                                {t("Flight.Flight Route")}: {fareRule.city_pair || "-"}
                            </span>
                        </Grid>
                        {fareRule.rule_details.map((detail, detailIndex) => (
                            <Accordion
                                key={detailIndex}
                                sx={{
                                    width: "100%",
                                    borderRadius: "0.625rem !important",
                                    backgroundColor: "var(--white-gray-color)",
                                    boxShadow: "0px 0px 1px 0px rgba(0, 0, 0, 0.60)",
                                    margin: "0 !important",
                                    "&::before": {
                                        display: "none",
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<GridExpandMoreIcon />}
                                    sx={{
                                        flexDirection: "row-reverse !important",
                                        columnGap: "1rem",
                                        borderRadius: "0.625rem",
                                        backgroundColor: "var(--white-gray-color)",
                                        "& .MuiTypography-root": {
                                            fontSize: "clamp(0.75rem, 1vw, 1rem)",
                                            color: "var(--teal-color-18)",
                                            fontWeight: "bold",
                                        },
                                        "& .MuiAccordionSummary-content": {
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        },

                                        "&.Mui-expanded": {
                                            backgroundColor: "transparent",
                                            "& .MuiTypography-root": {
                                                color: "var(--primary-color)",
                                            },
                                            "& .MuiAccordionSummary-expandIconWrapper": {
                                                path: {
                                                    stroke: "var(--primary-color)",
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <Typography>{detail.category === "Penalty" ? t("Flight.Penalty Rules") : detail.category}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: { xs: 0 }, textAlign: "justify" }}>
                                    <Box sx={{ direction: detail.message_direction, padding: "0 1rem" }}>
                                        <Typography dangerouslySetInnerHTML={{ __html: detail.rules }} />
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Grid>
                ))
            ) : (
                <Typography>{t("Toast Messages.Information Unavailable")}</Typography>
            )}
        </Grid>
    );
};

export default FareRulesModal;
